import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  HostListener,
  computed,
  effect,
  inject,
  signal,
} from '@angular/core';
import { NavigationEnd, Router, RouterLink, RouterOutlet } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { EMPTY } from 'rxjs';
import { filter, catchError, switchMap, take } from 'rxjs/operators';
import { ALT_TEXT, IMAGE_PATHS, NAVIGATION_TYPES } from '../../core/constants/common.constant';
import { Route } from '../../core/enums/route.enum';
import { NotificationDropdownComponent } from '../notification-dropdown/notification-dropdown.component';
import { NotificationService } from '../../services/notification/notification.service';
import { UserRole, UserService } from '../../services/user/user.service';
import { HeaderComponent } from '../header/header.component';
import { SidebarComponent } from '../sidebar/sidebar.component';
import {
  fetchNotificationsForDropdown,
  getCountryIdFromProfile,
  markNotificationsAsRead,
  resolveNotificationCountryId,
} from '@app/utils/notification.utils';
import { PartySearchContextService } from '@app/services/party-search/party-search-context.service';
import { internalReloadRedirectsToSearch } from '../../core/utils/route-access.util';
import { FeatureUpdateFeedbackComponent } from '@app/features/feature-update-feedback/feature-update-feedback.component';
import { AuthLogoutService } from '@app/services/auth/auth-logout.service';
import { LogoutFeedbackFlowService } from '@app/services/auth/logout-feedback-flow.service';
import { ScrollLockService } from '../../core/services/scroll-lock.service';

/**
 * Authenticated app shell: {@link HeaderComponent}, {@link SidebarComponent}, and primary
 * {@link RouterOutlet}. Handles mobile drawer, desktop sidebar collapse, and mobile
 * notification popover; {@link ChangeDetectionStrategy#OnPush} with signals.
 *
 * Responsibilities:
 * - Manages responsive sidebar state (mobile drawer vs. desktop collapse)
 * - Handles mobile notification popover separate from desktop header notifications
 * - Tracks current route for dashboard-specific UI adjustments
 * - Lazy-loads unread notification count on initialization
 * - Responds to navigation, viewport resize, and keyboard events (Escape key)
 */
@Component({
  selector: 'app-main-layout',
  standalone: true,
  imports: [
    RouterLink,
    RouterOutlet,
    HeaderComponent,
    SidebarComponent,
    NotificationDropdownComponent,
    FeatureUpdateFeedbackComponent,
  ],
  templateUrl: './main-layout.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MainLayoutComponent implements AfterViewInit {
  // ─── Constants ─────────────────────────────────────────────────────────────

  /** Viewport width (px) at or above which the layout uses the desktop sidebar pattern. */
  private static readonly DESKTOP_BREAKPOINT = 768;
  private static readonly HEADER_NOTIFICATIONS_VIEW_SOURCE = 'header-notification-view-all';

  // ─── Dependencies ──────────────────────────────────────────────────────────

  readonly notificationStore = inject(NotificationService);
  readonly elementRef = inject(ElementRef);
  readonly router = inject(Router);
  readonly userService = inject(UserService);

  // ─── Template Constants ────────────────────────────────────────────────────

  /** Source of truth for logo URLs and accessibility labels shown in the template. */
  readonly IMAGE_PATHS = IMAGE_PATHS;
  readonly ALT_TEXT = ALT_TEXT;

  // ─── Public State ──────────────────────────────────────────────────────────

  /** Snapshot of the authenticated user's profile (name, email, country, …). */
  readonly userProfile = this.userService.userProfile;

  /**
   * Canonical URL after each route navigation; drives {@link isDashboardPage}
   * and any route-sensitive UI behaviors throughout the layout.
   * Updated whenever a {@link NavigationEnd} event fires in the router.
   */
  readonly currentRoute = signal(this.router.url);

  /**
   * Count of unread notifications shown in the header/mobile badge.
   * Sourced from {@link NotificationService#notificationCount}.
   */
  readonly notificationCount = this.notificationStore.notificationCount;

  private readonly scrollLockService = inject(ScrollLockService);

  /**
   * Computed flag: true when the user is viewing the dashboard route.
   * Drives dashboard-specific UI styling/behavior (e.g., greeting visibility).
   * Exposed to {@link HeaderComponent} via its input.
   */
  readonly isDashboardPage = computed(() => this.currentRoute() === `/${Route.DASHBOARD}`);
  readonly featureUpdateCountryId = computed(() => this.resolveEffectiveCountryId() ?? 1);

  /**
   * Mobile/narrow layout state: true when the sidebar overlay drawer is open.
   * Desktop: sidebar is never a drawer; it can only collapse via {@link isDesktopSidebarCollapsed}.
   */
  readonly sidebarOpen = signal(false);

  /**
   * Desktop layout state: true when the sidebar is in icon-rail (collapsed) mode.
   * Mobile: always false (triggered by {@link onWindowResize} below the desktop breakpoint).
   */
  readonly isDesktopSidebarCollapsed = signal(false);
  /** True when the viewport is narrower than the desktop breakpoint. */
  readonly isMobileView = signal(globalThis.innerWidth < MainLayoutComponent.DESKTOP_BREAKPOINT);
  /** Mobile header: notifications popover (separate from the desktop header dropdown). */

  /**
   * Mobile header bar state: true when the notifications popover dropdown is visible.
   * Distinct from desktop header notifications (which are a dropdown in {@link HeaderComponent}).
   * Used in {@link toggleMobileNotificationDropdown} and {@link closeMobileNotificationDropdown}.
   */
  readonly isMobileNotificationDropdownOpen = signal(false);

  readonly showFeatureUpdateFeedback = inject(LogoutFeedbackFlowService).isFeedbackRequested;

  private readonly destroyRef = inject(DestroyRef);
  private readonly partySearchContext = inject(PartySearchContextService);
  private readonly authLogout = inject(AuthLogoutService);
  private readonly logoutFeedbackFlow = inject(LogoutFeedbackFlowService);

  // ─── Lifecycle ─────────────────────────────────────────────────────────────

  /**
   * Initialize layout state:
   * 1. Subscribe to router navigation events and update {@link currentRoute}
   * 2. Load unread notification count badge via {@link loadUnreadCount}
   */
  constructor() {
    // Update currentRoute on each successful navigation
    this.router.events
      .pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        takeUntilDestroyed(),
      )
      .subscribe(() => this.currentRoute.set(this.router.url));

    // Populate unread badge early so users see it before interacting
    this.loadUnreadCount();

    // Refresh unread count whenever a party search resolves the impersonated user.
    effect((onCleanup) => {
      const role = this.userService.userRoleGate();
      const isInternal = role === UserRole.CNHUser || role === UserRole.Admin;
      if (!isInternal) {
        return;
      }

      const userId = this.partySearchContext.impersonatedUserId();
      if (userId === undefined) {
        return;
      }

      const countryId = this.resolveEffectiveCountryId();
      if (countryId === null) {
        return;
      }

      const unreadCountSubscription = this.notificationStore
        .getUnreadCount(countryId, userId)
        .pipe(
          catchError(() => EMPTY),
          takeUntilDestroyed(this.destroyRef),
        )
        .subscribe();

      onCleanup(() => unreadCountSubscription.unsubscribe());
    });

    // Lock body scroll when sidebar opens on mobile
    effect(() => {
      const isSidebarOpen = this.sidebarOpen();
      const isMobile = this.isMobileView();

      if (isMobile && isSidebarOpen) {
        this.scrollLockService.lock();
      } else {
        this.scrollLockService.unlock();
      }
    });

    // On reload, CNHUser always restarts at Search; Admin stays on portal routes, Search only for customer URLs.
    if (this.isPageReload()) {
      this.userService
        .getUserRoleGate$()
        .pipe(
          take(1),
          catchError(() => EMPTY),
        )
        .subscribe((role) => {
          if (role && internalReloadRedirectsToSearch(role, this.router.config, this.router.url)) {
            void this.router.navigate([`/${Route.SEARCH_USER}`], { replaceUrl: true });
          }
        });
    }
  }

  // ─── Global Event Listeners ────────────────────────────────────────────────

  /**
   * Dismisses transient overlays whenever the user presses the Escape key.
   * Closes both the sidebar drawer and mobile notification popover.
   * Desktop header dropdown is handled inside {@link HeaderComponent}.
   */
  @HostListener('document:keydown.escape')
  onEscape(): void {
    this.closeSidebar();
    this.closeMobileNotificationDropdown();
  }

  /**
   * Closes the mobile notifications popover when the user clicks outside the layout element.
   * Implements click-outside-to-close UX for the mobile notification dropdown.
   * Desktop notifications are handled inside {@link HeaderComponent}.
   */
  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event): void {
    if (!this.isMobileNotificationDropdownOpen()) {
      return;
    }

    if (!this.elementRef.nativeElement.contains(event.target)) {
      this.closeMobileNotificationDropdown();
    }
  }

  /**
   * Responds to viewport resize; ensures responsive state is consistent.
   * Below {@link DESKTOP_BREAKPOINT}, mobile drawer mode is restored by uncollapsing the sidebar.
   * This prevents the collapsed icon-rail from being visible on mobile.
   */
  @HostListener('window:resize')
  onWindowResize(): void {
    const isMobile = globalThis.innerWidth < MainLayoutComponent.DESKTOP_BREAKPOINT;
    this.isMobileView.set(isMobile);
    if (isMobile) {
      this.isDesktopSidebarCollapsed.set(false);
    }
  }

  // ─── Sidebar & Mobile Drawer ───────────────────────────────────────────────
  ngAfterViewInit(): void {
    globalThis.performance?.mark?.('app-shell-rendered');
  }
  /**
   * Toggle the mobile sidebar drawer open/closed.
   * Only has effect on mobile; desktop sidebar uses {@link toggleDesktopSidebar}.
   */
  toggleSidebar(): void {
    this.sidebarOpen.update((open) => !open);
  }

  /**
   * Force the mobile sidebar drawer closed.
   * Used when user navigates (template redirects), presses Escape, or clicks outside.
   */
  closeSidebar(): void {
    this.sidebarOpen.set(false);
  }

  /**
   * Toggle desktop sidebar between expanded (full width) and collapsed (icon rail) mode.
   * Only has effect on desktop; mobile uses {@link toggleSidebar} for drawer UX.
   */
  toggleDesktopSidebar(): void {
    this.isDesktopSidebarCollapsed.update((collapsed) => !collapsed);
  }

  // ─── Mobile Notifications Popover ──────────────────────────────────────────

  /**
   * Toggle the mobile notification dropdown popover open/closed.
   * When opening, fetches the first page of notifications for display.
   * Marking as read is deferred until the dropdown closes.
   *
   * Note: Only available on mobile; desktop uses header dropdown via {@link HeaderComponent}.
   */
  toggleMobileNotificationDropdown(): void {
    const shouldOpen = !this.isMobileNotificationDropdownOpen();
    if (shouldOpen) {
      const selectedCountryId = getCountryIdFromProfile(this.userProfile?.());
      this.isMobileNotificationDropdownOpen.set(true);
      fetchNotificationsForDropdown(this.notificationStore, selectedCountryId)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe();
      return;
    }

    this.closeMobileNotificationDropdown();
  }

  /**
   * Force-close the mobile notification dropdown popover.
   * Triggered by click-outside event ({@link onDocumentClick}), Escape key ({@link onEscape}),
   * or navigation ({@link navigateToNotifications}).
   */
  closeMobileNotificationDropdown(): void {
    this.isMobileNotificationDropdownOpen.set(false);
    this.onMobileNotificationDropdownClose();
  }

  /**
   * Navigate to the full notifications page and dismiss the mobile popover.
   * Called when user clicks the "View All" button in the mobile dropdown.
   */
  navigateToNotifications(): void {
    this.closeMobileNotificationDropdown();
    this.router.navigate([`/${Route.NOTIFICATIONS}`], {
      queryParams: { source: MainLayoutComponent.HEADER_NOTIFICATIONS_VIEW_SOURCE },
    });
  }

  /**
   * Called when the mobile notification dropdown closes.
   * Marks all displayed notifications as read.
   */
  onMobileNotificationDropdownClose(): void {
    markNotificationsAsRead(this.notificationStore)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe();
  }

  /**
   * Called after all notifications are cleared from the mobile dropdown.
   * Refreshes the unread count badge from the server.
   */
  onMobileNotificationsCleared(): void {
    this.notificationStore
      .getUnreadCount(this.resolveEffectiveCountryId(), this.resolveImpersonatedUserId())
      .pipe(
        catchError(() => EMPTY),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  onFeatureUpdateFeedbackClosed(): void {
    const logoutOptions = this.logoutFeedbackFlow.completePendingLogout();
    if (!logoutOptions) {
      return;
    }

    this.authLogout.logout(logoutOptions);
  }

  // ─── Private Helpers ───────────────────────────────────────────────────────

  /**
   * Waits for the user profile to resolve before fetching the unread notification count,
   * so the country ID is always valid when the API call is made.
   */
  private loadUnreadCount(): void {
    const userId = this.resolveImpersonatedUserId();
    const countryId = this.resolveEffectiveCountryId();

    if (countryId !== null || this.isInternalPortalUser()) {
      this.notificationStore
        .getUnreadCount(countryId, userId)
        .pipe(
          catchError(() => EMPTY),
          takeUntilDestroyed(this.destroyRef),
        )
        .subscribe();
      return;
    }

    // Customer: wait for profile when country is not yet on the signal.
    this.userService
      .getUserProfile$()
      .pipe(
        take(1),
        switchMap((profile) =>
          this.notificationStore
            .getUnreadCount(
              resolveNotificationCountryId(
                this.userService.userRoleGate(),
                profile,
                this.partySearchContext.impersonatedCountryId(),
              ),
              userId,
            )
            .pipe(catchError(() => EMPTY)),
        ),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  /**
   * Country for notification APIs: impersonated customer when internal + party search;
   * profile country for customers; `null` for Admin/CNHUser with no party (API omits countryId).
   */
  private resolveEffectiveCountryId(): number | null {
    return resolveNotificationCountryId(
      this.userService.userRoleGate(),
      this.userProfile(),
      this.partySearchContext.impersonatedCountryId(),
    );
  }

  private isInternalPortalUser(): boolean {
    const role = this.userService.userRoleGate();
    return (
      role === UserRole.Admin ||
      role === UserRole.CNHUser ||
      role === UserRole.CNHMarketingUser ||
      role === UserRole.CNHNewProfileUser
    );
  }

  /**
   * Returns the impersonated customer's numeric user ID when the logged-in user
   * is a CNH internal user or Admin and a party search is active; otherwise `undefined`.
   */
  private resolveImpersonatedUserId(): number | undefined {
    const role = this.userService.userRoleGate();
    const isInternal = role === UserRole.CNHUser || role === UserRole.Admin;
    return isInternal ? this.partySearchContext.impersonatedUserId() : undefined;
  }

  private isPageReload(): boolean {
    try {
      const nav = globalThis.performance?.getEntriesByType?.('navigation')?.[0] as
        | PerformanceNavigationTiming
        | undefined;
      return nav?.type === NAVIGATION_TYPES.RELOAD;
    } catch {
      return false;
    }
  }
}
